import React from 'react';
import { VerificationProgress } from '../../types/kyb';

interface SidebarProps {
  progress: VerificationProgress;
  companyName: string;
  registrationNumber: string;
  taxId: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  progress,
  companyName,
  registrationNumber,
  taxId
}) => {
  return (
    <div className="w-72 bg-white border-r border-neutral-200 flex flex-col">
      <div className="p-8 flex flex-col items-center">
        <div className="relative w-36 h-36 mb-5">
          {/* Progress circle with gradient */}
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4D96FF" />
                <stop offset="100%" stopColor="#1A73E8" />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke="#EEF5FF"
              strokeWidth="12"
            />
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="12"
              strokeDasharray="276"
              strokeDashoffset={276 - (276 * progress.percentComplete) / 100}
              strokeLinecap="round"
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center flex-col">
            <span className="text-3xl font-bold text-primary-600">{progress.percentComplete}%</span>
          </div>
        </div>
        <div className="text-sm text-neutral-500 mb-2">Verification Progress</div>
        <div className="text-base font-semibold flex items-center text-neutral-800">
          <span className="text-primary-600 font-medium">Step {progress.currentStep}</span>
          <span className="mx-2 text-neutral-400">/</span>
          <span>{progress.totalSteps}</span>
        </div>
      </div>

      <div className="flex-1 px-5 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
            Verification Steps
          </div>
          <div className="text-xs text-primary-600 font-medium">
            {progress.stepsCompleted.filter(step => step.completed).length}/{progress.stepsCompleted.length}
          </div>
        </div>

        <div className="space-y-2.5">
          {progress.stepsCompleted.map(step => (
            <div
              key={step.id}
              className={`flex items-center py-2.5 px-3.5 rounded-xl transition-all ${
                step.inProgress
                  ? 'bg-primary-50 shadow-sm border border-primary-100'
                  : step.completed
                  ? 'hover:bg-neutral-50'
                  : 'hover:bg-neutral-50'
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full mr-3 flex items-center justify-center ${
                  step.completed
                    ? 'bg-primary-500 text-white shadow-sm'
                    : step.inProgress
                    ? 'border-2 border-primary-500 bg-white shadow-sm'
                    : 'border-2 border-neutral-300 bg-white'
                }`}
              >
                {step.completed && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                {step.inProgress && <div className="w-2 h-2 rounded-full bg-primary-500"></div>}
              </div>
              <div className="flex-1">
                <span className={`text-sm ${
                  step.inProgress
                    ? 'font-medium text-primary-700'
                    : step.completed
                    ? 'font-medium text-neutral-800'
                    : 'font-normal text-neutral-600'
                }`}>
                  {step.name}
                </span>
                
                {/* Progress indicator line - only for in-progress items */}
                {step.inProgress && (
                  <div className="w-full h-1 bg-primary-100 rounded-full mt-2">
                    <div 
                      className="h-full bg-primary-500 rounded-full" 
                      style={{ width: '60%' }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-5 border-t border-neutral-200">
        <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100 shadow-card">
          <div className="flex items-start mb-3">
            <div className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-neutral-800">{companyName}</div>
              <div className="text-xs text-neutral-500 mt-0.5">Verifying business credentials</div>
            </div>
          </div>
          <div className="flex items-center text-xs space-x-3 text-neutral-600">
            <div className="flex items-center">
              <span className="font-medium mr-1">ID:</span> 
              <span className="text-neutral-500">#{registrationNumber}</span>
            </div>
            <div className="h-3 w-px bg-neutral-300"></div>
            <div className="flex items-center">
              <span className="font-medium mr-1">Tax:</span> 
              <span className="text-neutral-500">{taxId}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;